import React, { useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Chip,
  Button,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
  alpha,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import useUser from "../../Core/hook/useUser";
import { Privileges } from "../../Core/Enum/enum";
import { useNotifier } from "../../Core/Notifier";
import { useCreateAttendanceRecordMutation } from "../../Api/attoDeskApi";
import type { ClassCardData } from "./ClassCard";
import OutstandingStar from "./OutstandingStar";

/**
 * Slide-in class detail drawer opened from a card click on the timetable.
 *
 * Renders every field in the spec (time, staff, mode, location, subject,
 * grade, capacity, roster, makeup metadata, billing warnings) and the four
 * permission-gated actions (attendance, assign, edit schedule, view full).
 *
 * Attendance controls:
 *   - Present / Absent / Late / No-Show (mapped to backend enum below)
 *   - Disabled for future-dated occurrences per spec.
 *   - Disabled entirely when the user lacks AttendanceRecord_Create.
 *   - No page refresh: the mutation invalidates the timetable tag so the
 *     summary counts refresh in the background.
 *
 * "No Show" maps to `absent` with a note — the existing enum is
 * (present|absent|late|excused) and adding a new value is out of scope
 * for the Today page (would ripple through reports).
 */

export type ClassDrawerData = ClassCardData & {
  scheduled_date: string;
  staff_id: number | null;
  staff_name?: string | null;
  staff_image?: string | null;
};

type AttendanceOption = "present" | "absent" | "late" | "no_show";

const ATTENDANCE_LABELS: Record<AttendanceOption, string> = {
  present: "Present",
  absent:  "Absent",
  late:    "Late",
  no_show: "No Show",
};

const ATTENDANCE_COLORS: Record<AttendanceOption, string> = {
  present: "#22c55e",
  absent:  "#ef4444",
  late:    "#eab308",
  no_show: "#f97316",
};

/**
 * Map a UI status to the backend attendance enum. The pair returned drives
 * both the persisted `status` and any note stored alongside (so a "No Show"
 * is auditable rather than looking identical to a plain absence).
 */
const toBackendStatus = (ui: AttendanceOption): { status: string; note: string | null } => {
  switch (ui) {
    case "no_show": return { status: "absent", note: "No show" };
    default:        return { status: ui, note: null };
  }
};

/**
 * Take the existing DB attendance value and reverse-map it to the UI
 * option (so a stored "absent" with a "No show" note re-appears as
 * "No Show" the next time the drawer opens).
 */
const fromBackendStatus = (dbStatus: string | null | undefined, note?: string | null): AttendanceOption | null => {
  if (!dbStatus) return null;
  if (dbStatus === "absent" && note && note.toLowerCase().includes("no show")) return "no_show";
  if (dbStatus === "present" || dbStatus === "absent" || dbStatus === "late") return dbStatus;
  return null;
};

type Props = {
  open: boolean;
  occurrence: ClassDrawerData | null;
  staffName?: string | null;
  canSeeFinance?: boolean;
  onClose: () => void;
};

const ClassDrawer: React.FC<Props> = ({ open, occurrence, staffName, canSeeFinance = false, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const { userHasRight } = useUser();
  const canRecordAttendance = userHasRight(Privileges.AttendanceRecordCreate) || userHasRight(Privileges.AttendanceRecordEdit);
  const canEditSchedule     = userHasRight(Privileges.ClassScheduleEdit);
  const canAssignStudent    = userHasRight(Privileges.ClassScheduleEdit);

  const [saveAttendance, { isLoading: saving }] = useCreateAttendanceRecordMutation();

  // Local optimistic overlay: keyed by student id. Merged with the server
  // snapshot so a save appears immediately even while the tag invalidation
  // is still flushing the new /timetable/week payload.
  const [pending, setPending] = useState<Record<number, AttendanceOption>>({});

  // Reset local overlay when the drawer flips to a different occurrence
  // (otherwise the last card's state would visually leak into the next).
  React.useEffect(() => {
    setPending({});
  }, [occurrence?.id]);

  // Attendance is only writable for today or past dates.
  const isFuture = useMemo(() => {
    if (!occurrence) return false;
    return dayjs(occurrence.scheduled_date).isAfter(dayjs(), "day");
  }, [occurrence]);

  const isCancelled = !!occurrence?.is_cancelled;
  const isMakeup    = !!occurrence?.is_makeup;
  const isOnline    = occurrence?.teaching_mode === "Online";
  const isHybrid    = occurrence?.teaching_mode === "Hybrid";

  const handleAttendance = async (studentId: number, ui: AttendanceOption) => {
    if (!occurrence) return;
    if (!canRecordAttendance) return;
    if (isFuture) return;

    // Optimistic local overlay.
    setPending((p) => ({ ...p, [studentId]: ui }));

    const mapped = toBackendStatus(ui);
    try {
      const res: any = await saveAttendance({
        occurrence_id: occurrence.id,
        records: [{
          studentId,
          status: mapped.status,
          notes:  mapped.note ?? undefined,
        }],
      }).unwrap();
      if (res?.status === false) {
        showErrorMessage(res.message || "Could not save attendance");
        setPending((p) => {
          const { [studentId]: _, ...rest } = p;
          return rest;
        });
      } else {
        showMessage(`${ATTENDANCE_LABELS[ui]} recorded`);
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || e?.message || "Could not save attendance");
      setPending((p) => {
        const { [studentId]: _, ...rest } = p;
        return rest;
      });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 540 },
          bgcolor: "#f8fafc",
        },
      }}
    >
      {/* ── Header ────────────────────────────────────────────── */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          px: 2.5, py: 2,
          position: "sticky", top: 0, zIndex: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, color: "text.secondary", textTransform: "uppercase" }}>
              Class Details
            </Typography>
            {occurrence ? (
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: "text.primary", lineHeight: 1.25, mt: 0.25 }}>
                {occurrence.class_name ?? occurrence.subject ?? "Untitled Class"}
              </Typography>
            ) : (
              <Skeleton width="70%" height={22} />
            )}
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close drawer">
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Status badges */}
        {occurrence && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5} sx={{ mt: 1 }}>
            {isCancelled && (
              <Chip label="Cancelled" size="small" sx={{ bgcolor: alpha("#ef4444", 0.15), color: "#b91c1c", fontWeight: 600 }} />
            )}
            {isMakeup && !isCancelled && (
              <Chip icon={<AutorenewOutlinedIcon sx={{ fontSize: 13 }} />} label="Makeup" size="small" sx={{ bgcolor: alpha("#eab308", 0.2), color: "#854d0e", fontWeight: 600 }} />
            )}
            <Chip
              icon={(isOnline || isHybrid) ? <WifiOutlinedIcon sx={{ fontSize: 13 }} /> : <LocationOnOutlinedIcon sx={{ fontSize: 13 }} />}
              label={isHybrid ? "Online + In-Person" : (isOnline ? "Online" : (occurrence.location_name ?? "In-Person"))}
              size="small"
              sx={{ bgcolor: alpha((isOnline || isHybrid) ? "#0ea5e9" : "#f97316", 0.15), color: (isOnline || isHybrid) ? "#0369a1" : "#9a3412" }}
            />
            {isHybrid && occurrence.location_name && (
              <Chip
                icon={<LocationOnOutlinedIcon sx={{ fontSize: 13 }} />}
                label={occurrence.location_name}
                size="small"
                sx={{ bgcolor: alpha("#f97316", 0.15), color: "#9a3412" }}
              />
            )}
            {!isCancelled && occurrence.available_spots > 0 && (
              <Chip label={`${occurrence.available_spots} Spot${occurrence.available_spots === 1 ? "" : "s"} Available`} size="small" sx={{ bgcolor: alpha("#22c55e", 0.18), color: "#166534", fontWeight: 600 }} />
            )}
            {occurrence.is_full && !isCancelled && (
              <Chip label="Full" size="small" sx={{ bgcolor: alpha("#ef4444", 0.15), color: "#b91c1c", fontWeight: 600 }} />
            )}
          </Stack>
        )}
      </Box>

      {/* ── Body ──────────────────────────────────────────────── */}
      <Box sx={{ p: 2.5 }}>
        {!occurrence ? (
          <Stack spacing={1.5}>
            <Skeleton height={80} />
            <Skeleton height={200} />
          </Stack>
        ) : (
          <>
            {/* Meta block */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", p: 2, mb: 2 }}>
              <MetaRow icon={<AccessTimeIcon fontSize="small" />} label="When">
                {dayjs(occurrence.scheduled_date).format("dddd, MMM D, YYYY")}
                {occurrence.start_time && (
                  <> · {formatTime(occurrence.start_time)} – {formatTime(occurrence.end_time)}</>
                )}
              </MetaRow>
              <MetaRow icon={<PersonOutlineIcon fontSize="small" />} label="Staff">
                {staffName ?? occurrence.staff_name ?? "—"}
              </MetaRow>
              <MetaRow icon={(isOnline || isHybrid) ? <WifiOutlinedIcon fontSize="small" /> : <LocationOnOutlinedIcon fontSize="small" />} label="Where">
                {isHybrid
                  ? `Online + In-Person${occurrence.location_name ? " · " + occurrence.location_name : ""}`
                  : (isOnline ? "Online" : (occurrence.location_name ?? "In-person (no location)"))}
              </MetaRow>
              {(occurrence.subject || occurrence.grade) && (
                <MetaRow icon={<GroupsOutlinedIcon fontSize="small" />} label="Subject / Grade">
                  {[occurrence.subject, occurrence.grade].filter(Boolean).join(" · ")}
                </MetaRow>
              )}
              <MetaRow icon={<EventAvailableOutlinedIcon fontSize="small" />} label="Capacity">
                {occurrence.assigned_count} / {occurrence.capacity || "—"} assigned
                {occurrence.available_spots > 0 && !isCancelled && (
                  <Typography component="span" sx={{ color: "#166534", fontWeight: 600, ml: 0.75, fontSize: 12 }}>
                    ({occurrence.available_spots} open)
                  </Typography>
                )}
              </MetaRow>
            </Box>

            {/* Action row */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<OpenInNewOutlinedIcon />}
                onClick={() => navigate(`/class-schedules/${occurrence.class_id ?? occurrence.id}`)}
                sx={{ textTransform: "none" }}
              >
                View Full Class
              </Button>
              {canAssignStudent && !isCancelled && occurrence.available_spots > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddAltOutlinedIcon />}
                  onClick={() => navigate(`/class-schedule/list?assignTo=${occurrence.id}`)}
                  sx={{ textTransform: "none" }}
                >
                  Assign Student
                </Button>
              )}
              {canEditSchedule && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => navigate(`/class-schedule/list?editOccurrence=${occurrence.id}`)}
                  sx={{ textTransform: "none" }}
                >
                  Edit Schedule
                </Button>
              )}
            </Stack>

            {/* Roster + attendance */}
            <Box sx={{ bgcolor: "#fff", borderRadius: "10px", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.3, color: "text.secondary", textTransform: "uppercase" }}>
                  Roster
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  {isFuture
                    ? "Attendance opens on the class date"
                    : canRecordAttendance
                      ? "Tap a status to record"
                      : "View-only"}
                </Typography>
              </Stack>

              {occurrence.students.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
                  <Typography sx={{ fontSize: 12.5 }}>
                    No students assigned yet.
                    {canAssignStudent && !isCancelled && occurrence.available_spots > 0
                      ? " Use Assign Student to fill open spots."
                      : ""}
                  </Typography>
                </Box>
              ) : (
                <Stack divider={<Divider flexItem />}>
                  {occurrence.students.map((s) => {
                    const initial = fromBackendStatus(s.attendance_status);
                    const current = pending[s.id] ?? initial ?? "";

                    return (
                      <Stack
                        key={s.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ px: 2, py: 1.25 }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                          <Avatar
                            onClick={() => navigate(`/student-history/${s.id}`)}
                            sx={{ width: 32, height: 32, fontSize: 12, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, cursor: "pointer" }}
                          >
                            {s.initials || "?"}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography
                                onClick={() => navigate(`/student-history/${s.id}`)}
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  lineHeight: 1.2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {s.name}
                              </Typography>
                              <OutstandingStar
                                hasOutstanding={s.has_outstanding_invoice}
                                invoice={(s as any).outstanding_invoice}
                                canSeeFinance={canSeeFinance}
                                size={14}
                              />
                            </Stack>
                            {s.attendance_status && (
                              <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
                                Currently: {s.attendance_status}
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={current}
                          onChange={(_, v) => v && handleAttendance(s.id, v)}
                          disabled={!canRecordAttendance || isFuture || isCancelled || saving}
                          sx={{ height: 28 }}
                        >
                          {(Object.keys(ATTENDANCE_LABELS) as AttendanceOption[]).map((opt) => (
                            <ToggleButton
                              key={opt}
                              value={opt}
                              sx={{
                                textTransform: "none",
                                px: 0.9,
                                fontSize: 10.5,
                                borderColor: alpha(ATTENDANCE_COLORS[opt], 0.4),
                                color: ATTENDANCE_COLORS[opt],
                                "&.Mui-selected": {
                                  bgcolor: alpha(ATTENDANCE_COLORS[opt], 0.18),
                                  color: ATTENDANCE_COLORS[opt],
                                  borderColor: ATTENDANCE_COLORS[opt],
                                  "&:hover": { bgcolor: alpha(ATTENDANCE_COLORS[opt], 0.25) },
                                },
                              }}
                            >
                              {ATTENDANCE_LABELS[opt]}
                            </ToggleButton>
                          ))}
                        </ToggleButtonGroup>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

const MetaRow: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ py: 0.75, "&:not(:last-of-type)": { borderBottom: "1px solid rgba(0,0,0,0.04)" } }}>
    <Box sx={{ color: "text.secondary", pt: 0.25, "& svg": { fontSize: 16 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4, color: "text.secondary", textTransform: "uppercase", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "text.primary", lineHeight: 1.35, mt: 0.15 }}>
        {children}
      </Typography>
    </Box>
  </Stack>
);

const formatTime = (t: string | null | undefined): string => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hh = parseInt(h, 10);
  const mer = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 || 12;
  return `${h12}:${m} ${mer}`;
};

export default ClassDrawer;
